<template>
  <div class="admin-conversation-list px-2 px-md-4 py-2 py-md-4">
    <ConveyTable
      showSearch
      showFilter
      showDateRange
      showExport
      tableClassName="admin-conversation-table"
      searchMode="validPhoneSearch"
      :items="conversations"
      :fields="fields"
      :placeholders="placeholders"
      :filterFields="filterFields"
      :filterOptions="filterOptions"
      :sortBy="sortBy"
      :sortDesc="sortDesc"
      :perPageOptions="perPageOptions"
      :perPage="perPage"
      :currentPage="currentPage"
      :totalRows="totalRows"
      :isLoading="isLoading"
      :isExportProcessing="isExportProcessing"
      @onFetch="(options) => fetchConversations(options, true)"
      @onExport="(options) => exportToCsv(options)"
    >
      <template #cell(status)="data">
        <span
          :class="
            `admin-conversation-table--${
              data.value
            }-text badge font-weight-medium text-capitalize ${
              isDarkMode ? 'dark' : ''
            }`
          "
        >
          {{ data.value }}
        </span>
      </template>
      <template #cell(language)="data">
        <div v-if="data.value" class="d-flex align-items-center">
          <LanguageCode :languageCode="data.item.language_code" />
          <span class="ml-2">
            {{ data.value }}
          </span>
        </div>
      </template>
      <template #cell(duration)="data">
        <span>{{ getDuration(data.item) }}</span>
      </template>
      <template #cell(channel)="data">
        <span v-if="data.value" class="convo-archived badge text-capitalize fw-bold">
          {{ data.value }}
        </span>
      </template>
      <template #cell(recipient)="data">
        <span
          class="admin-conversation-table--agentPhone"
          :class="isDarkMode ? 'dark' : ''"
        >
          <router-link
            v-if="$auth.can('read:conversation')"
            :to="`/admin/conversations/${data.item.conversation_id}`"
          >
            {{ prettyPhoneNumber(data.item.recipient_number) }}
          </router-link>
          <template v-else>
            {{ prettyPhoneNumber(data.item.recipient_number)}}
          </template>
        </span>
      </template>
      <template #cell(created_at)="data">
        <span>{{ formatDate(data.value, "MMM D, YYYY h:mm a") }}</span>
      </template>
      <template #cell(messages)="data">
        <span>{{ data.item.message_count }}</span>
      </template>
      <template #cell(closed_at)="data">
        <span>
          {{ formatDate(data.item.ended_at, "MMM D, YYYY h:mm a") }}
        </span>
      </template>
      <template #cell(direction)="data">
        <b-icon
          v-if="data.value === 'outbound'"
          v-b-tooltip.hover
          title="Outbound Conversation"
          icon="arrow-up-right"
        ></b-icon>
        <b-icon
          v-else
          v-b-tooltip.hover
          title="Inbound Conversation"
          icon="arrow-down-left"
        ></b-icon>
      </template>
      <template #cell(agent)="data">
        <span>{{ data.item.agent_name }}</span>
      </template>
    </ConveyTable>
  </div>
</template>

<script>
/* eslint-disable */
import { mapGetters } from "vuex";
import formatter from "@/mixins/formatter";
import errorHandler from "@/mixins/errorHandler";
import moment from "moment";
import {
  runGraphqlQuery,
  formatDuration,
  downloadCSV,
  getBaseGraphqlServerUrl,
} from "../../../graphql/utils";
import { makeConversationQuery } from "../../../graphql/queries";
import LanguageCode from "../../common/LanguageCode.vue";
import axios from "axios";
import ConveyTable from "@/components/ConveyTable";

export default {
  name: "AdminConversationList",
  mixins: [formatter, errorHandler],
  components: {
    LanguageCode,
    ConveyTable,
  },
  data() {
    return {
      conversations: [],
      isExportProcessing: false,
      fields: [
        {
          key: "direction",
          sortable: false,
          tdClass: "custom-column",
          label: "",
        },
        { key: "recipient", sortable: false },
        { key: "agent", sortable: false },
        { key: "duration", sortable: false },
        { key: "channel", sortable: false },
        { key: "language", sortable: false },
        { key: "status", sortable: false },
        { key: "closed_at", sortable: false },
        { key: "created_at", sortable: true },
      ],
      perPage: 10,
      perPageOptions: [5, 10, 25, 50, 100],
      currentPage: 1,
      totalRows: 0,
      isLoading: false,
      query: "",
      sortBy: "created_at",
      sortDesc: true,
      placeholders: {
        search: 'Search Conversations...',
        daterange: 'Select Date Range',
      },
      filterFields: [
        { name: 'channel', label: 'Channel' },
        { name: 'direction', label: 'Direction' },
        { name: 'status', label: 'Status' },
      ],
      filterOptions: {
        channel: [
          {
            value: "sms",
            text: "Sms",
            label: "Sms",
          },
          {
            value: "voice",
            text: "Voice",
            label: "Voice",
          },
          {
            value: "web",
            text: "Web",
            label: "Web",
          },
          {
            value: "media",
            text: "Media",
            label: "Media",
          },
          {
            value: "notification",
            text: "Notification",
            label: "Notification",
          },
        ],
        direction: [
          {
            value: "outbound",
            text: "Outbound",
            label: "Outbound",
          },
          {
            value: "inbound",
            text: "Inbound",
            label: "Inbound",
          },
        ],
        status: [
          // {
          //   value: "active",
          //   text: "Active",
          //   label: "Active",
          // },
          {
            value: "archived",
            text: "Archived",
            label: "Archived",
          },
          {
            value: "closed",
            text: "Closed",
            label: "Closed",
          },
        ],
      },
      fetchAbortController: null,
    };
  },
  computed: {
    ...mapGetters("user", ["currentToken", "isDarkMode"]),
    ...mapGetters("agency", ["currentAgencyId"]),
  },
  methods: {
    getDuration(data) {
      return formatDuration(data.duration);
    },
    abortPreviousFetch() {
      if (this.fetchAbortController) {
        this.fetchAbortController.abort();
        this.fetchAbortController = null;
      }
    },
    async fetchConversations(fetchOptions) {
      try {
        this.abortPreviousFetch();
        this.fetchAbortController = new AbortController();
        const {
          searchKey,
          startDate,
          endDate,
          perPage,
          currentPage,
          sortBy,
          sortDesc,
          filter
        } = fetchOptions;

        this.perPage = perPage;
        this.currentPage = currentPage;
        this.sortBy = sortBy;
        this.sortDesc = sortDesc;

        this.isLoading = true;
        const offset = (this.currentPage - 1) * perPage;

        const query = makeConversationQuery({
          startDate,
          endDate,
          offset,
          searchKey,
          orderBy: sortBy,
          sortDesc: sortDesc,
          perPage,
          filter,
        });
        const res = await runGraphqlQuery(query, this.fetchAbortController.signal);
        const responseData = res.data.data.getConversations;
        this.totalRows = responseData.total;
        this.conversations = responseData.data;
      } catch (err) {
        if (!this.isApiCanceled(err)) {
          console.log("[AdminConversationList:fetchConversations] ERROR: ", err);
        }
      } finally {
        this.isLoading = false;
      }
    },
    async exportToCsv(exportOptions) {
      try {
        const {
          searchKey,
          startDate,
          endDate,
          perPage,
          currentPage,
          sortBy,
          sortDesc,
          filter
        } = exportOptions;

        this.isExportProcessing = true;
        const baseURL = getBaseGraphqlServerUrl();
        const exportConversationUrl = `${baseURL}/export-conversations`;

        const requestBody = JSON.stringify({
          search_key: searchKey,
          filters: filter,
          start_date: startDate,
          end_date: endDate,
          sort_by_field: sortBy,
          sort_by_order: sortDesc ? "DESC" : "ASC",
        });

        const response = await axios.post(
          exportConversationUrl,
          requestBody,
          {
            responseType: "blob",
            headers: {
              Accept: "application/json",
              Authorization: `Bearer ${this.currentToken}`,
              "x-agency-id": `${this.currentAgencyId}`,
              "Content-Type": "application/json",
            },
          }
        );
        const blob = new Blob([response.data], { type: "text/csv" });
        this.isExportProcessing = false;
        downloadCSV(blob);
      } catch (error) {
        this.isExportProcessing = false;
      }
    },
  },
  beforeDestroy() {
    this.abortPreviousFetch();
  }
};
</script>

<style lang="scss">
.admin-conversation-list {
  width: 100%;
  height: 100%;
  overflow: hidden;

  .admin-conversation-table {
    &--agentName,
    &--customerName {
      color: #333843;
      font-weight: 500;
      font-size: 0.875rem;
      display: block;
    }
    &--agentPhone,
    &--customerPhone {
      color: #667085;
      font-weight: 400;
      font-size: 0.875rem;
      display: block;
      &.dark {
        a {
          color: #9dc7ff !important;
        }
      }
    }
    &--inbound-text {
      color: #0d894f;
      font-size: 0.875rem;
      background: #e7f4ee;
      font-weight: 500;
      padding: 4px 12px 4px 12px;
      border-radius: 8px;
    }
    &--outbound-text {
      color: #e46a11;
      font-size: 0.875rem;
      background: #fdf1e8;
      font-weight: 600;
      padding: 4px 12px 4px 12px;
      border-radius: 8px;
      &.dark {
        color: #ba490f;
        background: #e7cab4;
      }
    }
    &--archived-text {
      color: #fff;
      font-size: 0.875rem;
      background: #ababab;
      font-weight: 600;
      padding: 4px 12px 4px 12px;
      border-radius: 8px;
      &.dark {
        background: #9194a7 !important;
      }
    }
    &--active-text {
      color: #0d894f;
      font-size: 0.875rem;
      background: #e7f4ee;
      font-weight: 600;
      padding: 4px 12px 4px 12px;
      border-radius: 8px;
      &.dark {
        color: #0d894f;
        background: #dae5e0;
      }
    }
    &--closed-text {
      color: #f04438;
      font-size: 0.875rem;
      background: #feedec;
      font-weight: 600;
      padding: 4px 12px 4px 12px;
      border-radius: 8px;
      &.dark {
        color: #ba490f;
        background: #e7cab4;
      }
    }
  }
}
</style>
